// A member of staff's feedback on an event they worked (POST /feedback/mine), opened
// from the banner on My details and My events (components/FeedbackBanner.jsx).
//
// The same questions as the public form (components/FeedbackForm.jsx), with who they
// are, how to reach them, the event and their shift shown rather than asked: the
// server takes all four from their record and attendance, whatever is sent.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import FeedbackForm from "./FeedbackForm";
import { emptyFeedbackForm, staffFeedbackBody, submissionProblems } from "../feedbackRules";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";

export default function StaffFeedbackDialog({ event, person, onClose, onSent }) {
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState(emptyFeedbackForm);
  const [showErrors, setShowErrors] = useState(false);
  const { busy, run } = useWriteLock();

  useEffect(() => {
    if (event) { setForm(emptyFeedbackForm()); setShowErrors(false); }
  }, [event]);

  if (!event) return null;
  const problems = submissionProblems(form, { staff: true });
  const name = person ? [person.forename, person.surname].filter(Boolean).join(" ") : "";
  const contact = person ? [person.phone, person.email].filter(Boolean).join("  ·  ") : "";

  const send = () => {
    setShowErrors(true);
    if (problems.length) return;
    run(() => axios.post(`${API_BASE_URL}/feedback/mine`, staffFeedbackBody(form, event.event_id))
      .then(res => {
        notify(`Thank you — your feedback was sent (${res.data.reference}).`, "success");
        onSent?.();
        onClose();
      }));
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Feedback on {event.label}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          How did the event go? Praise, suggestions and concerns all help us run the next one
          better. Only the details are required.
        </Alert>
        <FeedbackForm mode="staff" form={form} setForm={setForm} showErrors={showErrors}
                      fixed={{ name, contact, eventLabel: event.label,
                               shiftDetails: event.shift_details }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={busy}>
          {busy ? "Sending…" : "Send feedback"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
